import avatars from '../config/avatars';
import { avatarColors } from '../config/avatarColors';
import { makeClassName } from '@siteless/util';
import { useEffect, useRef } from 'react';
import CheckIcon from './CheckIcon';
import { Tooltip } from './Tooltip';
import useCopy from '../util/useCopy';
import { SingleLanguageCopyType } from '../assets/copy';

type AvatarId = keyof typeof avatars;

type AvatarValue = {
  avatarBackgroundHex: string | null | undefined;
  avatarId: AvatarId | undefined;
};

let AvatarGrid = ({
  selectedAvatarId,
  selectedColor,
  onSelectAvatar,
  selectedAvatarRef,
}: {
  selectedAvatarId: AvatarId | undefined;
  selectedColor: string | null | undefined;
  onSelectAvatar: (avatarId: AvatarId) => void;
  selectedAvatarRef: React.RefObject<HTMLDivElement | null>;
}) => {
  return (
    <div className="grid grid-cols-4 gap-3 xs:grid-cols-5 sm:grid-cols-6">
      {Object.values(avatars).map((avatar) => {
        let isSelected = selectedAvatarId === avatar.id;
        let ItemArtwork = avatar.ArtworkComponent;

        return (
          <div key={avatar.id} ref={isSelected ? selectedAvatarRef : undefined}>
            <button
              type="button"
              onClick={() => onSelectAvatar(avatar.id)}
              className={makeClassName(
                'relative aspect-square w-full rounded-full transition-all',
                isSelected
                  ? 'ring-4 ring-rose-bright ring-offset-2'
                  : 'hover:scale-105',
              )}
            >
              <ItemArtwork
                className="h-full w-full rounded-full"
                backgroundColor={selectedColor || undefined}
              />
              {isSelected && (
                <div className="absolute -right-[5%] -top-[5%] flex h-[30%] w-[30%] items-center justify-center rounded-full bg-rose-bright text-white shadow">
                  <CheckIcon className="h-[60%] w-[60%]" />
                </div>
              )}
            </button>
          </div>
        );
      })}
    </div>
  );
};

let ColorPicker = ({
  selectedColor,
  onSelectColor,
  size = 'small',
  sidePadding = 16,
}: {
  selectedColor: string | null | undefined;
  onSelectColor: (color: string | null) => void;
  size?: 'small' | 'large';
  sidePadding?: number;
}) => {
  let sizeClass = size === 'large' ? 'h-8 w-8' : 'h-7 w-7';
  let gradientHeight = size === 'large' ? 'h-8' : 'h-7';

  return (
    <div className="flex items-center overflow-auto py-1">
      <div
        className={makeClassName(
          'sticky left-0 z-10 flex-none bg-gradient-to-r from-neutral-0 to-transparent',
          gradientHeight,
        )}
        style={{ width: sidePadding }}
      />
      <button
        type="button"
        onClick={() => onSelectColor(null)}
        className={makeClassName(
          'mr-2 flex flex-none items-center justify-center rounded-full border-2 border-dashed transition-all',
          sizeClass,
          selectedColor === null
            ? 'border-purple-500 bg-purple-100'
            : 'border-neutral-300',
        )}
        aria-label="No background color"
      >
        {selectedColor === null && (
          <div className="h-2 w-2 rounded-full bg-purple-500" />
        )}
      </button>
      {avatarColors.map((color) => (
        <button
          type="button"
          key={color}
          onClick={() => onSelectColor(color)}
          className={makeClassName(
            'mr-2 flex-none rounded-full transition-all',
            sizeClass,
            selectedColor === color
              ? 'ring-2 ring-rose-bright ring-offset-2'
              : '',
          )}
          style={{ backgroundColor: color }}
          aria-label={`Select color ${color}`}
        />
      ))}
      <div
        className={makeClassName(
          'sticky right-0 flex-none bg-gradient-to-l from-neutral-0 to-transparent',
          gradientHeight,
        )}
        style={{ width: sidePadding }}
      />
    </div>
  );
};

let SelectedAvatarInfo = ({
  avatar,
  color,
  embedded = false,
}: {
  avatar: (typeof avatars)[AvatarId];
  color: string | null | undefined;
  embedded?: boolean;
}) => {
  let copy = useCopy();
  let ArtworkComponent = avatar.ArtworkComponent;

  if (!embedded) {
    return (
      <>
        <div className="flex justify-center">
          <ArtworkComponent
            className="h-36 w-36 rounded-full shadow-lg"
            backgroundColor={color || undefined}
          />
        </div>
        <div className="mt-4 text-center">
          <div className="text-xl font-bold text-purple-800">{avatar.name}</div>
          <div className="whitespace-nowrap text-sm text-neutral-500">
            {avatar.fromMovie} · {avatar.fromYear}
            {'helpTextKey' in avatar && avatar.helpTextKey && (
              <>
                {' · '}
                <Tooltip
                  text={
                    copy[
                      avatar.helpTextKey as keyof SingleLanguageCopyType
                    ] as string
                  }
                  tooltipSide="bottom"
                  className="inline"
                >
                  <span className="cursor-pointer underline decoration-dotted">
                    Hint
                  </span>
                </Tooltip>
              </>
            )}
          </div>
        </div>
      </>
    );
  }

  return (
    <div className="flex w-full flex-none items-center gap-3 px-[30px] pb-2 pt-3">
      <ArtworkComponent
        className="h-16 w-16 flex-none rounded-full shadow"
        backgroundColor={color || undefined}
      />
      <div className="min-w-0 flex-1">
        <div className="truncate text-lg font-bold text-purple-800">
          {avatar.name}
        </div>
        <div className="flex text-base text-neutral-500">
          <span className="truncate">{avatar.fromMovie}</span>
          <span className="flex-none">&nbsp;· {avatar.fromYear}</span>
          {'helpTextKey' in avatar && avatar.helpTextKey && (
            <span className="flex-none">
              &nbsp;·&nbsp;
              <Tooltip
                text={
                  copy[
                    avatar.helpTextKey as keyof SingleLanguageCopyType
                  ] as string
                }
                tooltipSide="bottom"
                className="inline"
              >
                <span className="cursor-pointer underline decoration-dotted">
                  Hint
                </span>
              </Tooltip>
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export let AvatarInput = ({
  value,
  onChange,
  className,
  embedded = false,
}: {
  value: AvatarValue;
  onChange: (value: AvatarValue & { avatarId: AvatarId }) => void;
  className?: string;
  embedded?: boolean;
}) => {
  let selectedAvatarRef = useRef<HTMLDivElement>(null);
  let scrollContainerRef = useRef<HTMLDivElement>(null);
  let selectedAvatar = value.avatarId ? avatars[value.avatarId] : null;

  // get selected avatar into view
  useEffect(() => {
    let container = scrollContainerRef.current;
    let selected = selectedAvatarRef.current;
    if (!container || !selected) return;
    container.scrollTop =
      selected.offsetTop -
      container.offsetTop -
      container.clientHeight / 2 +
      selected.clientHeight / 2;
  }, []);

  let handleSelectAvatar = (avatarId: AvatarId) => {
    onChange({
      avatarId,
      avatarBackgroundHex: value.avatarBackgroundHex,
    });
  };

  let handleSelectColor = (color: string | null) => {
    if (value.avatarId) {
      onChange({
        avatarId: value.avatarId,
        avatarBackgroundHex: color,
      });
    }
  };

  if (embedded) {
    return (
      <div
        className={makeClassName(
          'flex h-full w-full flex-col items-center',
          className,
        )}
      >
        <div className="min-h-0 w-full flex-1 overflow-auto border-y-4 border-neutral-100 px-4 pb-2 pt-4">
          <AvatarGrid
            selectedAvatarId={value.avatarId}
            selectedColor={value.avatarBackgroundHex}
            onSelectAvatar={handleSelectAvatar}
            selectedAvatarRef={selectedAvatarRef}
          />
        </div>

        {selectedAvatar && (
          <SelectedAvatarInfo
            avatar={selectedAvatar}
            color={value.avatarBackgroundHex}
            embedded
          />
        )}

        <div className="w-full flex-none py-2">
          <ColorPicker
            selectedColor={value.avatarBackgroundHex}
            onSelectColor={handleSelectColor}
            size="small"
            sidePadding={30}
          />
        </div>
      </div>
    );
  }

  return (
    <div
      className={makeClassName(
        'flex min-h-0 flex-1 flex-col overflow-x-hidden',
        className,
      )}
    >
      <div className="flex-none border-b-4 border-neutral-100 px-4 pb-3 pt-8">
        {selectedAvatar ? (
          <SelectedAvatarInfo
            avatar={selectedAvatar}
            color={value.avatarBackgroundHex}
          />
        ) : (
          <div className="flex h-36 items-center justify-center">
            <div className="text-neutral-400">Select an avatar</div>
          </div>
        )}

        <div className="relative -mx-4 mt-4 w-[calc(100%+2rem)]">
          <ColorPicker
            selectedColor={value.avatarBackgroundHex}
            onSelectColor={handleSelectColor}
            size="large"
          />
        </div>
      </div>

      <div
        ref={scrollContainerRef}
        className="min-h-0 flex-1 overflow-auto px-4 py-4"
      >
        <AvatarGrid
          selectedAvatarId={value.avatarId}
          selectedColor={value.avatarBackgroundHex}
          onSelectAvatar={handleSelectAvatar}
          selectedAvatarRef={selectedAvatarRef}
        />
      </div>
    </div>
  );
};
